import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Switch,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { adminApi } from '../services/api';
import FeatureSelector from './FeatureSelector';

interface OrganizationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const OrganizationDialog: React.FC<OrganizationDialogProps> = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    domain: '',
    maxRunsPerMonth: 50,
    sharedRunPool: true,
    accountManagerName: '',
    accountManagerEmail: '',
  });
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = ['Basic Information', 'Features'];

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleDisplayNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = event.target.value;
    
    // Auto-generate organization name from display name (kebab-case)
    const autoName = displayName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    setFormData(prev => ({
      ...prev,
      displayName,
      name: autoName
    }));
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate basic info
    if (!formData.displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (!formData.name.trim()) {
      setError('Organization name is required');
      return;
    }

    // Validate email format if provided
    if (formData.accountManagerEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.accountManagerEmail.trim())) {
      setError('Invalid email format for account manager email');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await adminApi.createOrganization({
        name: formData.name.trim(),
        displayName: formData.displayName.trim(),
        domain: formData.domain.trim() || undefined,
        maxRunsPerMonth: formData.maxRunsPerMonth || 50,
        sharedRunPool: formData.sharedRunPool,
        features: selectedFeatures,
        accountManagerName: formData.accountManagerName.trim() || undefined,
        accountManagerEmail: formData.accountManagerEmail.trim() || undefined,
      });

      // Reset form
      setFormData({
        name: '',
        displayName: '',
        domain: '',
        maxRunsPerMonth: 50,
        sharedRunPool: true,
        accountManagerName: '',
        accountManagerEmail: '',
      });
      setSelectedFeatures([]);
      setActiveStep(0);

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Validate before moving to next step
    if (activeStep === 0) {
      if (!formData.displayName.trim()) {
        setError('Display name is required');
        return;
      }
      if (!formData.name.trim()) {
        setError('Organization name is required');
        return;
      }
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        displayName: '',
        domain: '',
        maxRunsPerMonth: 50,
        sharedRunPool: true,
        accountManagerName: '',
        accountManagerEmail: '',
      });
      setSelectedFeatures([]);
      setActiveStep(0);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Create New Organization</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Display Name"
              value={formData.displayName}
              onChange={handleDisplayNameChange}
              required
              fullWidth
              autoFocus
              disabled={loading}
              helperText="Human-readable name for the organization"
            />

            <TextField
              label="Organization ID"
              value={formData.name}
              onChange={handleChange('name')}
              required
              fullWidth
              disabled={loading}
              helperText="Auto-generated from display name (e.g., 'My Company' → 'my-company')"
            />

            <TextField
              label="Domain"
              value={formData.domain}
              onChange={handleChange('domain')}
              fullWidth
              disabled={loading}
              placeholder="example.com"
              helperText="Optional: Email domain for auto-assignment"
            />

            <TextField
              label="Monthly Run Limit"
              type="number"
              value={formData.maxRunsPerMonth}
              onChange={handleChange('maxRunsPerMonth')}
              required
              fullWidth
              disabled={loading}
              InputProps={{
                endAdornment: <InputAdornment position="end">runs</InputAdornment>,
              }}
              inputProps={{ min: 1 }}
              helperText="Maximum number of jobs per month for users in this organization"
            />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.sharedRunPool}
                    onChange={(e) => setFormData(prev => ({ ...prev, sharedRunPool: e.target.checked }))}
                    disabled={loading}
                  />
                }
                label="Shared Run Pool"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                When enabled, all members share the organization's monthly run limit.
                When disabled, each member uses their own individual allocation.
              </Typography>
            </Box>

            <Box sx={{ pt: 2 }}>
              <TextField
                label="Account Manager Name"
                value={formData.accountManagerName}
                onChange={handleChange('accountManagerName')}
                fullWidth
                disabled={loading}
                placeholder="e.g., John Smith"
                helperText="Optional: Name of the person managing this account"
              />
            </Box>

            <TextField
              label="Account Manager Email"
              type="email"
              value={formData.accountManagerEmail}
              onChange={handleChange('accountManagerEmail')}
              fullWidth
              disabled={loading}
              placeholder="e.g., john.smith@example.com"
              helperText="Optional: Email address for support inquiries"
            />
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              General features are enabled by default. You can customize which features this organization has access to.
            </Alert>
            <FeatureSelector
              selectedFeatures={selectedFeatures}
              onChange={setSelectedFeatures}
              disabled={loading}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button 
            variant="contained" 
            onClick={handleNext} 
            disabled={loading}
          >
            Next
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Creating...' : 'Create Organization'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationDialog;
